import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useApiQuery } from '@/modules/common';
import { Routes } from '@/router/routes';

import {
  AIConfigurationTab,
  InfoTab,
  InventoryTab,
  MasksTab,
  ModelsTab,
  OrdersHistoryTab,
  ParticipantsTab,
  PrintersTab,
  SmsHistoryTab,
  StylesTab,
  VideoTab,
} from '../components';
import { EventDetailsTab } from '../constants';
import { EventType } from '../types';

export const useEventDetailsTabs = ({
  selectedTypes,
}: {
  selectedTypes: EventType[];
}) => {
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const { isFetching: isFetchingTypes } = useApiQuery<EventType[]>(
    `/events/types`,
    {
      queryKey: ['eventTypes'],
      initialData: null,
    }
  );

  const visibleTabs = useMemo(
    () => [
      ...new Set(
        [
          ...new Set(selectedTypes?.flatMap((type) => {
            return Object.keys(type.requiredFields);
          })),
          EventDetailsTab.Info,
        ]
      )
    ],
    [selectedTypes]
  );

  const onChangeTab = (tab: EventDetailsTab) => {
    navigate(`${Routes.EventsV2}/${params.id}/${tab}`);
  };

  const tabsConfig = useMemo(
    () => [
      {
        value: EventDetailsTab.Info,
        label: 'Info',
        title: 'Basic Info',
        Component: InfoTab,
      },
      {
        value: EventDetailsTab.AIConfiguration,
        label: 'AI Configuration',
        title: 'Models',
        Component: AIConfigurationTab,
      },
      {
        value: EventDetailsTab.Video,
        label: 'Video',
        title: 'Event Video',
        Component: VideoTab,
      },
      {
        value: EventDetailsTab.Models,
        label: 'Models',
        Component: ModelsTab,
      },
      {
        value: EventDetailsTab.Styles,
        label: 'Styles',
        Component: StylesTab,
      },
      {
        value: EventDetailsTab.Masks,
        label: 'Masks',
        Component: MasksTab,
      },
      {
        value: EventDetailsTab.Printers,
        label: 'Printers',
        Component: PrintersTab,
      },
      {
        value: EventDetailsTab.Participants,
        label: 'Participants',
        Component: ParticipantsTab,
      },
      {
        value: EventDetailsTab.Inventory,
        label: 'Inventory',
        Component: InventoryTab,
      },
      {
        value: EventDetailsTab.OrdersHistory,
        label: 'Orders History',
        Component: OrdersHistoryTab,
      },
      {
        value: EventDetailsTab.SMSHistory,
        label: 'SMS History',
        Component: SmsHistoryTab,
      },
    ],
    []
  );

  // if tab is not visible, redirect to first visible tab
  useEffect(() => {
    if (isFetchingTypes) {
      return;
    }

    if (!visibleTabs.includes(params.tab)) {
      navigate(`${Routes.EventsV2}/${params.id}/${visibleTabs[0]}`);
    }
  }, [visibleTabs, params.tab, isFetchingTypes]);

  return {
    tabsConfig,
    visibleTabs,
    activeTab: params.tab,
    isFetchingTypes,
    onChangeTab,
    onBack: () => navigate(Routes.EventsV2),
  };
};
